'use client'

import * as React from 'react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { toast } from '@/lib/toast'
import { apiClient } from '@/lib/api-client'

interface FeedbackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const [feedback, setFeedback] = useState('')
  const [category, setCategory] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!feedback.trim()) {
      toast.error('Please enter your feedback')
      return
    }

    if (feedback.trim().length > 5000) {
      toast.error('Feedback message is too long (max 5000 characters)')
      return
    }

    if (!category) {
      toast.error('Please select a category')
      return
    }

    setIsSubmitting(true)

    try {
      // Frontend API Schema:
      // POST /feedback
      // Authorization: Bearer <token>
      // Body: { message: string (required, max 5000), category: string (optional) }
      // Response: 201 Created with feedback object
      // Errors: 429 (rate limit), 401 (unauthorized), 400 (bad request)

      const feedbackData = {
        message: feedback.trim(),
        category: category,
      }

      // Make API call to submit feedback
      await apiClient.submitFeedback(feedbackData)

      toast.success('Thank you for your feedback!')
      setFeedback('')
      setCategory('')
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to submit feedback:', error)

      // Handle specific error cases based on API schema
      if (error instanceof Error && 'statusCode' in error) {
        const apiError = error as { statusCode: number; message: string }
        if (apiError.statusCode === 429) {
          toast.error('Too many requests. Please try again later.')
        } else if (apiError.statusCode === 401) {
          toast.error('You must be logged in to submit feedback.')
        } else if (apiError.statusCode === 400) {
          toast.error(
            apiError.message ||
              'Invalid feedback data. Please check your input.'
          )
        } else {
          toast.error('Failed to send feedback. Please try again.')
        }
      } else {
        toast.error('Failed to send feedback. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send us feedback</DialogTitle>
          <DialogDescription>
            We value your feedback to help improve ThoughtFabric. Share your
            thoughts, suggestions, or report any issues you&apos;re
            experiencing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Select
              value={category}
              onValueChange={setCategory}
              disabled={isSubmitting}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bug">Bug Report</SelectItem>
                <SelectItem value="feature">Feature Request</SelectItem>
                <SelectItem value="general">General Feedback</SelectItem>
                <SelectItem value="improvement">
                  Improvement Suggestion
                </SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="feedback">Your Feedback</Label>
              <span className="text-xs text-muted-foreground">
                {feedback.length}/5000
              </span>
            </div>
            <Textarea
              id="feedback"
              placeholder="How can we improve ThoughtFabric?"
              aria-label="Send feedback"
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              className="min-h-[120px]"
              disabled={isSubmitting}
              maxLength={5000}
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-black text-white dark:bg-white dark:text-black hover:bg-neutral-800 dark:hover:bg-neutral-200"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send feedback'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
